import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, FormGroup, FormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { Producto } from 'app/models/producto.models';
import { Venta } from 'app/models/venta.models';
import { VoucherTypes } from 'app/models/vouchertype.models';
import { ComprobantesService } from 'app/services/comprobantes.service';
import { VentasService } from 'app/services/ventas.service';
import { ImpresionService } from 'app/services/impresion.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-compras',
  templateUrl: './compras.component.html',
  styleUrls: ['./compras.component.css']
})
export class ComprasComponent implements OnInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  displayedColumns: string[] = [];
  dataSource: MatTableDataSource<any> = new MatTableDataSource();
  compras = [];
  date = new UntypedFormControl();
  promedio = 0;
  totalVentas = 0;
  totalFacturas = 0;
  totalComprobantes = 0;
  totalItems = 0;
  pageSize = 20;
  currentPage = 0;
  loading: boolean;
  voucherTypes: VoucherTypes[] = [];
  
  // Filtros
  filtroForm: FormGroup;
  tiposComprobante = [
    { value: 'FACTURAS A', name: 'Factura A' },
    { value: 'FACTURAS B', name: 'Factura B' },
    { value: 'FACTURAS C', name: 'Factura C' },
    { value: 'NOTAS DE CREDITO A', name: 'Nota de Crédito A' }, 
    { value: 'NOTAS DE CREDITO B', name: 'Nota de Crédito B' },
    { value: 'NOTAS DE DEBITO A', name: 'Nota de Débito A' },
    { value: 'NOTAS DE DEBITO B', name: 'Nota de Débito B' }
  ];
  filtrosAplicados = false;

  constructor(
    private ventasService: VentasService,
    private comprobanteService: ComprobantesService,
    public dialog: MatDialog,
    private _snackBar: MatSnackBar,
    private router: Router,
    private fb: FormBuilder,
    private impresionService: ImpresionService
  ) {}

  ngOnInit(): void {
    const sidebar = <HTMLElement>document.querySelector('.navbar-brand');
    if (sidebar !== undefined) {
      sidebar.innerHTML = 'Compras';
    }
    this.initFilterForm();
    this.configurarColumnasIniciales();
    this.getCompras();
  }

  /**
   * Configura las columnas iniciales de la tabla
   */
  configurarColumnasIniciales(): void {
    this.displayedColumns = ['numero', 'fecha', 'cliente', 'enviado', 'electronico', 'total', 'accion'];
  }

  /**
   * Configura las columnas dinámicamente según los datos cargados
   */
  configurarColumnasDinamicas(): void {
    // Verificar que compras sea un array válido
    if (!Array.isArray(this.compras) || this.compras.length === 0) {
      this.displayedColumns = ['numero', 'fecha', 'cliente', 'enviado', 'electronico', 'total', 'accion'];
      return;
    }

    // Siempre mantener la columna total, pero el template maneja mostrar "-" para remitos
    // Esto permite manejar casos mixtos donde hay remitos y otros comprobantes
    this.displayedColumns = ['numero', 'fecha', 'cliente', 'enviado', 'electronico', 'total', 'accion'];
    
    // Si solo hay remitos en toda la lista, podríamos quitar la columna total
    const soloRemitos = this.compras.length > 0 && this.compras.every(compra => this.esRemito(compra));
    if (soloRemitos) {
      this.displayedColumns = ['numero', 'fecha', 'cliente', 'enviado', 'electronico', 'accion'];
    }
  }

  /**
   * Determina si un comprobante es un remito
   */
  esRemito(comprobante: any): boolean {
    // Verificar por código de voucher type (más preciso)
    const voucherCode = comprobante?.header?.receiptBook?.voucherType?.code || 
                       comprobante?.voucherType?.code;
    
    // Verificar por nombre del tipo de comprobante
    const voucherTypeName = comprobante?.header?.receiptBook?.voucherType?.voucherTypesCode?.name?.toLowerCase() ||
                           comprobante?.voucherType?.voucherTypesCode?.name?.toLowerCase() ||
                           comprobante?.voucherType?.name?.toLowerCase() ||
                           comprobante?.typecomprobante?.nombre?.toLowerCase() ||
                           '';
    
    // Verificar también en el número/texto del comprobante
    const comprobanteTexto = comprobante?.numero?.toLowerCase() || 
                            comprobante?.comprobante?.toLowerCase() || 
                            comprobante?.name?.toLowerCase() || 
                            '';
    
    // Códigos típicos de remitos y criterios de identificación
    const codigosRemito = ['R', 'REM', 'REMITO'];
    const nombresRemito = ['remito', 'delivery'];
    
    // Verificar en código
    if (codigosRemito.includes(voucherCode)) {
      return true;
    }
    
    // Verificar en nombre del tipo
    if (voucherTypeName && nombresRemito.some(nombre => voucherTypeName.includes(nombre))) {
      return true;
    }
    
    // Verificar en texto del comprobante
    if (comprobanteTexto && nombresRemito.some(nombre => comprobanteTexto.includes(nombre))) {
      return true;
    }
    
    return false;
  }

  /**
   * Inicializa el formulario de filtros
   */
  initFilterForm(): void {
    this.filtroForm = this.fb.group({
      fechaDesde: [''],
      fechaHasta: [''],
      cliente: [''],
      proveedor: [''],
      tipoComprobante: [''],
      estado: [''],
      enviado: [''],
      electronico: ['']
    });
  }

  /**
   * Maneja los cambios de página del paginador
   */
  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    // Si necesitas paginación del servidor, aquí puedes llamar a getCompras() con parámetros
  }

  /**
   * Obtiene las compras desde el servidor
   */
  getCompras(): void {
    this.loading = true;
    this.comprobanteService.getCompras().subscribe(data => {
      console.log('Datos recibidos del API:', data);
      
      // Verificar si los datos están anidados en una propiedad 'data'
      const comprasArray = data?.data || data;
      
      // Asegurar que sea un array
      this.compras = Array.isArray(comprasArray) ? comprasArray : [];
      this.dataSource.data = this.compras;
      this.dataSource.paginator = this.paginator;
      this.dataSource.sort = this.sort;
      this.configurarColumnasDinamicas();
      
      // Configurar el total de items para el paginador
      this.totalItems = this.compras.length;
      
      console.log('Compras procesadas:', this.compras);
      console.log('DataSource data:', this.dataSource.data);
      console.log('Total items:', this.totalItems);
      
      this.loading = false;
    }, err => {
      console.log('Error al obtener compras:', err);
      this.compras = [];
      this.dataSource.data = [];
      this.totalItems = 0;
      this.loading = false;
    });
  }

  /**
   * Aplica filtro de búsqueda
   */
  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  /**
   * Obtiene el nombre del proveedor
   */
  getProveedorNombre(compra: any): string {
    if (compra?.supplier?.businessName) {
      return compra.supplier.businessName;
    }
    
    if (compra?.supplier?.firstName && compra?.supplier?.lastName) {
      return `${compra.supplier.firstName} ${compra.supplier.lastName}`;
    }
    
    if (compra?.supplier?.firstName) {
      return compra.supplier.firstName;
    }
    
    return 'Sin proveedor';
  }

  /**
   * Navega al detalle de la compra
   */
  navigateToDetail(compraId: string): void {
    if (compraId) {
      this.router.navigate(['/compras/detalle', compraId]);
    }
  }

  /**
   * Obtiene la clase CSS para el tipo de comprobante
   */
  getTipoClass(tipoComprobante: string): string {
    // Aplicar el estilo de factura a todos los comprobantes
    return 'tipo-factura';
  }

  /**
   * Obtiene la clase CSS para el estado de envío
   */
  getEstadoEnvioClass(comprobante: any): string {
    return comprobante?.sent ? 'envio-enviado' : 'envio-pendiente';
  }

  /**
   * Obtiene la clase CSS para el estado electrónico
   */
  getEstadoElectronicoClass(comprobante: any): string {
    return comprobante?.electronic ? 'electronico-si' : 'electronico-no';
  }

  /**
   * Obtiene la clase CSS para el total
   */
  getTotalClass(total: number): string {
    if (!total || total === 0) {
      return 'total-neutro';
    } else if (total > 0) {
      return 'total-positivo';
    } else {
      return 'total-negativo';
    }
  }

  /**
   * Obtiene la clase CSS para la fila según el tipo de comprobante
   */
  getRowClass(comprobante: any): string {
    const tipo = comprobante?.voucherType?.name?.toLowerCase() || '';
    
    if (tipo.includes('factura')) {
      return 'row-factura';
    } else if (tipo.includes('credito')) {
      return 'row-credito';
    } else if (tipo.includes('debito')) {
      return 'row-debito';
    } else if (tipo.includes('remito')) {
      return 'row-remito';
    } else if (tipo.includes('pedido')) {
      return 'row-pedido';
    }
    
    return '';
  }

  /**
   * Función para aplicar filtros
   */
  aplicarFiltros(): void {
    console.log('Aplicando filtros:', this.filtroForm.value);
    this.filtrosAplicados = true;
  }

  /**
   * Función para limpiar filtros
   */
  limpiarFiltros(): void {
    this.filtroForm.reset();
    this.filtrosAplicados = false;
    this.getCompras();
  }

  /**
   * Formatea la fecha para mostrar
   */
  formatearFecha(fecha: string): string {
    if (!fecha) return '';
    const date = new Date(fecha);
    return date.toLocaleDateString('es-AR');
  }

  /**
   * Formatea el número de comprobante
   */
  formatearNumero(prefix: string, number: number): string {
    if (!prefix || !number) return '';
    return `${prefix}-${number.toString().padStart(8, '0')}`;
  }

  /**
   * Formatea el importe total
   */
  formatearTotal(total: number): string {
    if (!total) return '$0.00';
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(total);
  }

  /**
   * Obtiene el estado del envío
   */
  getEstadoEnvio(comprobante: any): string {
    return comprobante?.sent ? 'Enviado' : 'Pendiente';
  }

  /**
   * Obtiene el estado electrónico
   */
  getEstadoElectronico(comprobante: any): string {
    return comprobante?.electronic ? 'Sí' : 'No';
  }

  /**
   * Obtiene la clase de estado para badges
   */
  getEstadoBadgeClass(estado: string): string {
    if (estado === 'Enviado') {
      return 'badge-success';
    } else if (estado === 'Pendiente') {
      return 'badge-warning';
    }
    return 'badge-secondary';
  }

  /**
   * Maneja el clic en una fila de la tabla
   */
  onRowClick(compra: any): void {
    this.navigateToDetail(compra.id);
  }

  /**
   * Maneja el clic en el proveedor
   */
  onProveedorClick(event: Event, compra: any): void {
    event.stopPropagation();
    this.navigateToSupplier(compra.supplier?.id);
  }

  /**
   * Maneja el clic en editar
   */
  onEditClick(event: Event, compra: any): void {
    event.stopPropagation();
    this.editarCompra(compra);
  }

  /**
   * Maneja el clic en eliminar
   */
  onDeleteClick(event: Event, compra: any): void {
    event.stopPropagation();
    this.eliminar(compra);
  }

  /**
   * Verifica si hay filtros aplicados
   */
  hayFiltrosAplicados(): boolean {
    const formValue = this.filtroForm.value;
    return Object.values(formValue).some(value => value && value !== '');
  }

  /**
   * Obtiene el texto del estado según las propiedades
   */
  getEstadoTexto(comprobante: any): string {
    if (comprobante?.electronic) {
      return comprobante?.sent ? 'Electrónico - Enviado' : 'Electrónico - Pendiente';
    } else {
      return comprobante?.sent ? 'Manual - Enviado' : 'Manual - Pendiente';
    }
  }

  /**
   * Abre el snackbar con un mensaje
   */
  openSnackBar(message: string, action: string): void {
    this._snackBar.open(message, action, {
      duration: 3000,
      panelClass: ['success-snackbar']
    });
  }

  /**
   * Abre el modal para crear/editar compra
   */
  openDialog(): void {
    // Implementar apertura de diálogo
    console.log('Abriendo diálogo para nueva compra');
  }

  /**
   * Edita una compra existente
   */
  editarCompra(compra: any): void {
    // Implementar edición de compra
    console.log('Editando compra:', compra);
  }

  /**
   * Elimina una compra
   */
  eliminar(comprobante: any): void {
    Swal.fire({
      title: `¿Desea eliminar el comprobante seleccionado?`,
      text: `Eliminar Comprobante`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Si, eliminalo!',
      cancelButtonText: 'No'
    }).then((result) => {
      if (result.isConfirmed) {
        this.comprobanteService.eliminarComprobante(comprobante.id).subscribe(
          (data) => { 
            this.getCompras();
            this.openSnackBar(`${comprobante.typecomprobante?.nombre || 'Comprobante'} eliminado correctamente`, 'Información');
          },
          (err) => {
            console.log(err);
            this.openSnackBar(`Error al eliminar el ${comprobante.typecomprobante?.nombre || 'comprobante'}`, 'Información');
          }
        );
      }
    })
  }

  /**
   * Navega al detalle del proveedor
   */
  navigateToSupplier(supplierId: string): void {
    if (supplierId) {
      this.router.navigate(['/clientes/resumen', supplierId]);
    } else {
      this.openSnackBar('ID de proveedor no válido', 'Cerrar');
    }
  }

  /**
   * Determina si debe mostrar el total para un comprobante
   * Los remitos normalmente no muestran total
   */
  debeMostrarTotal(comprobante: any): boolean {
    const esRemito = this.esRemito(comprobante);
    
    // Debug: log para verificar la detección de remitos
    if (esRemito) {
      console.log('Comprobante detectado como remito:', {
        comprobante: comprobante,
        numero: comprobante?.numero,
        voucherCode: comprobante?.voucherType?.code,
        voucherTypeName: comprobante?.voucherType?.name,
        typecomprobante: comprobante?.typecomprobante?.nombre
      });
    }
    
    // No mostrar total para remitos
    if (esRemito) {
      return false;
    }
    
    // Verificar si tiene un monto total válido
    const total = comprobante?.totalAmount || comprobante?.total || 0;
    return total !== null && total !== undefined;
  }

  /**
   * Imprime un comprobante de compra
   * @param voucher Comprobante a imprimir
   */
  imprimirComprobante(voucher: any): void {
    if (!voucher || !voucher.id) {
      this.openSnackBar('Error: Comprobante no válido para imprimir', 'Cerrar');
      return;
    }

    // Usar el servicio de impresión centralizado
    this.impresionService.imprimirComprobante(voucher.id, 'compras');
  }

}
